import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Circle, CreditCard, ExternalLink, Heart, Loader2, MessageCircle, Compass } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { RichTextEditor } from './RichTextEditor';
import { clientWelcomeAPI, isClientViewMode } from '../lib/api';

const PAGOS = [['sepa', 'Domiciliación bancaria SEPA'], ['transferencia', 'Transferencia bancaria']];
const DISCORD_TEXT = 'Te hemos creado un canal privado en nuestro Discord. Es por donde te avisaremos cuando tengas algo que aprobar y por donde puedes escribirnos en el día a día. Pulsa el enlace de invitación, crea tu cuenta de Discord si no tienes, y entra. Puedes usarlo desde el móvil o desde el ordenador.';

const Check = ({ ok, busy, onClick, testId, disabled }) => (
  <button type="button" disabled={busy || disabled} onClick={onClick} className="mt-0.5 shrink-0 disabled:cursor-not-allowed" data-testid={testId} aria-label={ok ? 'Marcar como pendiente' : 'Marcar como hecho'}>
    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-muted-foreground" />}
  </button>
);

const DiscordCard = ({ data, onToggle, busy, ro }) => (
  <Card className={data.discord_joined ? 'border-emerald-200 bg-emerald-50/40' : ''} data-testid="welcome-discord-card">
    <CardContent className="p-5 flex gap-3">
      <Check ok={data.discord_joined} busy={busy === 'discord'} disabled={ro} onClick={() => onToggle(!data.discord_joined)} testId="welcome-discord-toggle" />
      <div className="space-y-3 min-w-0">
        <p className="font-semibold flex items-center gap-2"><MessageCircle className="w-5 h-5 text-[#5865F2]" />Tu canal de Discord</p>
        <a href={data.discord_invite_url} target="_blank" rel="noreferrer" className="text-sm text-[#5865F2] underline break-all" data-testid="welcome-discord-link">{data.discord_invite_url}</a>
        <p className="text-sm text-muted-foreground">{DISCORD_TEXT}</p>
        <p className="text-xs text-muted-foreground">Cuando ya estés dentro, marca este paso como hecho.</p>
      </div>
    </CardContent>
  </Card>
);

const PaymentCard = ({ data, onChoose, busy, ro }) => (
  <Card className={data.forma_pago ? 'border-emerald-200 bg-emerald-50/40' : ''} data-testid="welcome-payment-card">
    <CardContent className="p-5 flex gap-3">
      <Check ok={!!data.forma_pago} busy={busy === 'pago'} disabled testId="welcome-payment-status" />
      <div className="space-y-3 min-w-0 flex-1">
        <p className="font-semibold flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary" />Forma de pago</p>
        <p className="text-sm text-muted-foreground">Elige cómo prefieres pagar nuestras facturas.</p>
        <div className="flex flex-wrap gap-2">
          {PAGOS.map(([v, l]) => (
            <button key={v} type="button" disabled={ro || busy === 'pago'} onClick={() => onChoose(v)} data-testid={`welcome-pago-option-${v}`}
              className={`px-4 h-9 rounded-full border text-sm transition-colors disabled:cursor-not-allowed ${data.forma_pago === v ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>{l}</button>
          ))}
        </div>
        {data.forma_pago === 'sepa' && <p className="text-sm text-muted-foreground">Cobraremos cada factura mediante domiciliación en tu cuenta.</p>}
        {data.forma_pago === 'transferencia' && data.banco && (data.banco.iban || data.banco.titular) && (
          <div className="text-sm rounded-lg bg-muted/50 p-3 space-y-0.5" data-testid="welcome-bank-details">
            {data.banco.titular && <p><span className="text-muted-foreground">Titular:</span> {data.banco.titular}</p>}
            {data.banco.iban && <p><span className="text-muted-foreground">IBAN:</span> <span className="font-mono">{data.banco.iban}</span></p>}
            {data.banco.banco && <p><span className="text-muted-foreground">Banco:</span> {data.banco.banco}</p>}
            {data.banco.concepto && <p><span className="text-muted-foreground">Concepto:</span> {data.banco.concepto}</p>}
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const StepRow = ({ p, i, ok, busy, onToggle, ro }) => {
  const navigate = useNavigate();
  return (
    <div className={`flex gap-3 rounded-lg border p-3 transition-colors ${ok ? 'bg-emerald-50/60 border-emerald-200' : ''}`} data-testid={`welcome-step-${i}`}>
      <Check ok={ok} busy={busy === p.id} disabled={ro} onClick={() => onToggle(p.id, !ok)} testId={`welcome-step-toggle-${i}`} />
      <div className="flex-1 space-y-2 min-w-0">
        <p className={`text-sm font-medium ${ok ? 'line-through text-muted-foreground' : ''}`}>{p.titulo}</p>
        {p.descripcion && <p className="text-sm text-muted-foreground whitespace-pre-line" data-testid={`welcome-step-desc-${i}`}>{p.descripcion}</p>}
        {p.boton_url && (
          <a href={p.boton_url} target="_blank" rel="noreferrer" data-testid={`welcome-step-button-${i}`}>
            <Button size="sm" variant="outline">{p.boton_texto || 'Abrir'} <ExternalLink className="w-3.5 h-3.5 ml-1.5" /></Button>
          </a>
        )}
      </div>
      {p.enlace && <Button variant="ghost" size="sm" onClick={() => navigate(p.enlace)} data-testid={`welcome-step-link-${i}`}>Ir <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>}
    </div>
  );
};

const StepsCard = ({ data, onToggle, busy, ro }) => (
  <Card data-testid="welcome-steps-card">
    <CardContent className="p-5 space-y-4">
      <p className="font-semibold">Primeros pasos</p>
      {data.pasos.map((p, i) => <StepRow key={p.id} p={p} i={i} ok={data.completed_steps.includes(p.id)} busy={busy} onToggle={onToggle} ro={ro} />)}
    </CardContent>
  </Card>
);

export default function ClientWelcomePage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const ro = isClientViewMode();

  useEffect(() => { clientWelcomeAPI.me().then(setData).catch((e) => toast.error(e.message)); }, []);

  const run = async (key, fn) => {
    setBusy(key);
    try { setData(await fn()); } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  if (!data) return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const pct = data.total_count ? Math.round((data.completed_count / data.total_count) * 100) : 100;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5 max-w-4xl mx-auto" data-testid="client-welcome-page">
      <h1 className="text-3xl font-bold flex items-center gap-2"><Heart className="w-7 h-7 text-primary" />Bienvenida</h1>
      <Card className="bg-gradient-to-br from-emerald-50 to-background" data-testid="welcome-message">
        <CardContent className="p-6"><RichTextEditor value={data.mensaje_html} editable={false} testId="welcome-message-content" /></CardContent>
      </Card>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm"><span className="font-medium">Tu progreso</span><span className="text-muted-foreground" data-testid="welcome-steps-progress">{data.completed_count} de {data.total_count}</span></div>
        <Progress value={pct} className="h-2" />
      </div>
      <div className={`grid gap-4 ${data.discord_invite_url ? 'md:grid-cols-2' : ''}`}>
        {data.discord_invite_url && <DiscordCard data={data} busy={busy} ro={ro} onToggle={(h) => run('discord', () => clientWelcomeAPI.setDiscord(h))} />}
        <PaymentCard data={data} busy={busy} ro={ro} onChoose={(v) => run('pago', () => clientWelcomeAPI.setPago(v))} />
      </div>
      <StepsCard data={data} busy={busy} ro={ro} onToggle={(id, h) => run(id, () => clientWelcomeAPI.setStep(id, h))} />
      {data.all_done && <p className="text-sm text-emerald-700 font-medium" data-testid="welcome-all-done">¡Todo listo! Ya podemos arrancar.</p>}
      {data.portal_html && (
        <Card data-testid="welcome-portal-guide">
          <CardContent className="p-5 space-y-2">
            <p className="font-semibold flex items-center gap-2"><Compass className="w-5 h-5 text-primary" />Cómo funciona tu portal</p>
            <RichTextEditor value={data.portal_html} editable={false} testId="welcome-portal-content" />
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}
